import React from "react";
import { Edit3, ChevronDown } from "lucide-react";
import Dropdown from "../../../components/Dropdown";
import { TextField, DropdownField, SpinnerField, TextareaField } from "./FormFields";
import styles from "../css/inventoryItemDetail.module.css";
import { useAuth } from "../../../context/AuthContext";

export const FrontShopLayout = ({
  data,
  qty,
  setQty,
  reorder,
  setReorder,
  packSize,
  setPackSize,
  expiryDisplay,
  hasMultipleExpiry,
  setShowExpiryModal,
  category,
  setCategory,
  customCategory,
  setCustomCategory,
  categoryOptions,
  supplierOptions,
  supplierValue,
  onChangeSupplierName,
  onChangeSupplierCode,
  onChangeInvoiceNumber,
  onChangeCostPrice,
  onChangeNote,
  shelfValue,
  onChangeShelfNumber,
  readOnly = false,
}) => {
  const { user } = useAuth();
  const rawSku = String(data?.sku ?? "");
  const bc = user?.branchcode ? String(user.branchcode).trim() : "";
  const skuDisplay = bc && !rawSku.startsWith(`${bc} - `) ? `${bc} - ${rawSku}` : rawSku;

  // Helper: format yyyy-mm-dd to dd/mm/yyyy for UI display only
  const formatDateDMY = (yyyyMmDd) => {
    const s = String(yyyyMmDd || '').trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return s;
    return `${m[3]}/${m[2]}/${m[1]}`;
  };
  return (
    <>
      {/* Front Shop layout */}
      {/* Row 1 - matches image order */}
      <div className={styles.fsRowFiveA}>
        <TextField label="SKU" value={skuDisplay} readOnly />
        <SpinnerField
          label="Pack Size"
          value={packSize}
          onChange={readOnly ? undefined : setPackSize}
          min={0}
          step={1}
          ariaLabel="Pack Size"
          compact
          readOnly={readOnly}
        />
        <SpinnerField
          label="Quantity"
          value={qty}
          onChange={readOnly ? undefined : setQty}
          min={0}
          step={1}
          ariaLabel="Quantity"
          compact
          readOnly={readOnly}
        />
        <div className={styles.field}>
          <div className={styles.icdLabelRow}>
            <label>Expiry</label>
            {!readOnly && (
            <button
              type="button"
              className={styles.icdEditBtn}
              onClick={() => setShowExpiryModal(true)}
              aria-label="Edit Expiry"
            >
              <Edit3 size={16} />
            </button>
            )}
          </div>
          <div
            className={styles.icdDisplay}
            role="button"
            tabIndex={0}
            onClick={() => setShowExpiryModal(true)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowExpiryModal(true)}
            aria-label="Open Expiry editor"
          >
            <input
              type="text"
              value={expiryDisplay}
              readOnly
              className={`${styles.icdReadonlyInput} ${styles.expiryReadonlyInput}`}
              aria-label="Selected expiry dates"
              onClick={() => setShowExpiryModal(true)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowExpiryModal(true)}
            />
            {hasMultipleExpiry && (
              <span className={styles.icdCaret} aria-hidden>
                <ChevronDown size={16} />
              </span>
            )}
          </div>
        </div>
      </div>

      <hr className={styles.divider} />

      {/* Row 2 */}
      <div className={styles.fsRowFiveB}>
        <SpinnerField
          label="Reorder Level"
          value={reorder}
          onChange={readOnly ? undefined : setReorder}
          min={0}
          step={1}
          ariaLabel="Reorder Level"
          compact
          readOnly={readOnly}
        />
        <TextField label="Cost Price" value={data.costPrice} compact onChange={readOnly ? undefined : (e) => onChangeCostPrice?.(e.target.value)} readOnly={readOnly} />
        <TextField label="Bar Code" value={data.barcode || data.nappi} readOnly />
        <DropdownField
          label="Shelve Number"
          options={[
            "Warehouse A, Shelf 101",
            "Warehouse A, Shelf 102",
            "Front Shop, Shelf 716170019",
            "Front Shop, Shelf 716170419"
          ]}
          value={shelfValue ?? data.binNumber}
          onChange={readOnly ? undefined : (val) => onChangeShelfNumber?.(val)}
          readOnly={readOnly}
        />
        <TextField label="Last Reorder" value={formatDateDMY(data.lastReorder)} readOnly />
        <div className={`${styles.field} ${category === "Other" ? styles.frontShopCategoryNarrow : ""}`}>
          <label>Category</label>
          <div className={styles.categoryFieldRow}>
            <div className={styles.categorySelectWrap}>
              {readOnly ? (
                <input type="text" value={category} readOnly />
              ) : (
                <Dropdown
                  options={categoryOptions}
                  value={category}
                  onChange={(val) => {
                    setCategory(val);
                    if (val !== "Other") setCustomCategory("");
                  }}
                />
              )}
            </div>
            {!readOnly && category === "Other" && (
              <input
                className={`${styles.categoryOtherInput} ${styles.frontShopOtherInputNarrow}`}
                type="text"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                placeholder="Enter category"
              />
            )}
          </div>
        </div>
      </div>

      <hr className={styles.divider} />

      {/* Row 3 (Supplier details, no manufacturer field) */}
      <div className={styles.fsRowFour}>
        <DropdownField
          label="Supplier"
          options={Array.isArray(supplierOptions) && supplierOptions.length > 0
            ? supplierOptions
            : ["Pharma Dynamics", "MediSupply", "Diabetes Care", "VitaCare", "BeautyCare"]}
          value={supplierValue ?? data.supplier}
          onChange={readOnly ? undefined : (val) => onChangeSupplierName?.(val)}
          readOnly={readOnly}
        />
        <TextField label="Supplier code" value={data.supplierCode} onChange={readOnly ? undefined : (e) => onChangeSupplierCode?.(e.target.value)} readOnly={readOnly} />
        <TextField label="Invoice Number" value={data.invoiceNumber} onChange={readOnly ? undefined : (e) => onChangeInvoiceNumber?.(e.target.value)} readOnly={readOnly} />
      </div>

      <hr className={styles.divider} />

      {/* Notes */}
      <div className={styles.row}>
        <TextareaField label="Note" value={data.note} full onChange={readOnly ? undefined : (e) => onChangeNote?.(e.target.value)} readOnly={readOnly} />
      </div>
    </>
  );
};
